import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CREATE_jwel, tenantName } from "../../Config/Config";
import axios from "axios";

const C = {
  purple: "#4c3494",
  purple2: "#7c5cbf",
  purple3: "#ede9ff",
  teal: "#0d9488",
  teal2: "#ccfbf1",
  pink: "#ec4899",
  pink2: "#fce7f3",
  amber: "#f59e0b",
  amber2: "#fef3c7",
  blue: "#3b82f6",
  blue2: "#dbeafe",
  indigo: "#6366f1",
  indigo2: "#e0e7ff",
  text: "#1e1b4b",
  textSub: "#6b7280",
};

const TENANT_NAME = tenantName;
const API_HEADERS = {
  "Content-Type": "application/json",
  tenantName: TENANT_NAME,
};

/* ── buildUpdateString ─────────────────────────────────────────────────────── */
function buildUpdateString(form, dealer) {
  const fields = [
    { col: "FIRMNAME", val: form.firmName },
    { col: "CODE", val: form.firmCode },
    { col: "ADD1", val: form.address1 },
    { col: "ADD2", val: form.address2 },
    { col: "CITY", val: form.city },
    { col: "EMAIL1", val: form.pincode },
    { col: "FYEAR", val: form.financialYear },
    { col: "FCODE", val: form.financialYearCode },
    { col: "FMOBILE", val: form.firmPhone },
    { col: "OWNERNAME", val: form.ownerName },
    { col: "MOBILE", val: form.mobile1 },
    { col: "MOBILE1", val: form.mobile2 },
    { col: "EMAIL", val: form.email },
    { col: "TINNO", val: form.gstin },
    { col: "BRANCHNAME", val: form.branchName },
    { col: "CLIENTID", val: form.clientId },
    { col: "COMPOSITIONDLR", val: dealer === "composition" ? "true" : "false" },
  ];
  return fields
    .map(({ col, val }) => `${col}='${(val || "").replace(/'/g, "''")}'`)
    .join(",");
}

/* ── ShimmerLine ───────────────────────────────────────────────────────────── */
const ShimmerLine = ({
  colors = [C.purple, C.blue, C.teal, C.pink, C.amber],
  height = 3,
}) => (
  <motion.div
    style={{
      height,
      borderRadius: height,
      background: `linear-gradient(90deg, ${colors.join(", ")}, ${colors[0]})`,
      backgroundSize: "300% 100%",
    }}
    animate={{ backgroundPosition: ["0% 0%", "100% 0%", "0% 0%"] }}
    transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
  />
);

/* ── SectionDivider ────────────────────────────────────────────────────────── */
const SectionDivider = ({ icon, title, accent }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      margin: "4px 0 14px",
    }}
  >
    <motion.div
      animate={{ rotate: [0, 8, -8, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      style={{
        width: 28,
        height: 28,
        borderRadius: 7,
        flexShrink: 0,
        background: `linear-gradient(135deg,${accent},${accent}bb)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        boxShadow: `0 3px 10px ${accent}44`,
      }}
    >
      {icon}
    </motion.div>
    <span
      style={{
        fontSize: "clamp(12px,0.7vw,14px)",
        fontWeight: 800,
        color: accent,
        textTransform: "uppercase",
        letterSpacing: "1.4px",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </span>
    <div
      style={{
        flex: 1,
        height: 1,
        background: `linear-gradient(90deg,${accent}44,transparent)`,
      }}
    />
    <motion.div
      animate={{ opacity: [1, 0.3, 1], scale: [1, 1.5, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
      style={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: accent,
        boxShadow: `0 0 6px ${accent}`,
        flexShrink: 0,
      }}
    />
  </div>
);

/* ══════════════════════════════════════════════════════════════════════════════
   VALIDATORS
══════════════════════════════════════════════════════════════════════════════ */
const phoneError = (v) => {
  if (!v.length) return "Required";
  if (!/^[6-9]/.test(v)) return "Must start with 6, 7, 8 or 9";
  if (v.length < 10) return "Must be 10 digits";
  return "";
};
const phoneFilter = (v) =>
  /^\d{0,10}$/.test(v) && (v.length === 0 || /^[6-9]/.test(v));

const VALIDATORS = {
  /* ── plain text — required only ── */
  firmName: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },
  firmCode: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },
  address1: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },
  address2: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },
  city: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },
  ownerName: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },
  branchName: {
    filter: () => true,
    error: (v) => (v.trim() ? "" : "Required"),
  },
  clientId: { filter: () => true, error: (v) => (v.trim() ? "" : "Required") },

  /* ── GSTIN: 15-char, strict format ── */
  gstin: {
    filter: (v) => v.length <= 15,
    error: (v) => {
      if (!v.trim()) return "Required";
      if (v.length < 15) return "GSTIN must be 15 characters";
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(v))
        return "Invalid GSTIN format";
      return "";
    },
  },

  /* ── Pincode: 6 digits ── */
  pincode: {
    filter: (v) => /^\d{0,6}$/.test(v),
    error: (v) => {
      if (!v.length) return "Required";
      if (v.length < 6) return "Must be 6 digits";
      return "";
    },
  },

  /* ── Financial Year: 4-digit number ── */
  financialYear: {
    filter: (v) => /^\d{0,4}$/.test(v),
    error: (v) => {
      if (!v.length) return "Required";
      if (v.length < 4) return "Must be 4 digits";
      return "";
    },
  },

  /* ── Year Code: 4 digits ── */
  financialYearCode: {
    filter: (v) => /^\d{0,4}$/.test(v),
    error: (v) => {
      if (!v.length) return "Required";
      if (v.length < 4) return "Must be 4 digits";
      return "";
    },
  },

  /* ── Phone fields ── */
  firmPhone: { filter: phoneFilter, error: phoneError },
  mobile1: { filter: phoneFilter, error: phoneError },
  mobile2: { filter: phoneFilter, error: phoneError },

  /* ── Email ── */
  email: {
    filter: () => true,
    error: (v) => {
      if (!v.trim()) return "Required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
        return "Enter a valid email (e.g. firm@example.com)";
      return "";
    },
  },
};

/* ── numeric inputMode fields ── */
const NUMERIC_FIELDS = new Set([
  "pincode",
  "financialYear",
  "financialYearCode",
  "firmPhone",
  "mobile1",
  "mobile2",
]);

/* ══════════════════════════════════════════════════════════════════════════════
   FIELD
══════════════════════════════════════════════════════════════════════════════ */
const Field = ({
  fieldKey,
  label,
  placeholder,
  value,
  onChange,
  icon,
  accent = C.purple,
  accBg = C.purple3,
}) => {
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);

  const validator = VALIDATORS[fieldKey];

  const handleChange = (e) => {
    const raw = e.target.value;
    if (validator && !validator.filter(raw)) return; // silently block
    onChange(e);
  };

  const handleBlur = () => {
    setFocused(false);
    setTouched(true);
  };

  const errorMsg = touched && validator ? validator.error(value) : "";
  const isEmpty = value === "" || value == null;
  const isValid = touched && !isEmpty && !errorMsg;

  const borderColor = errorMsg
    ? "#ef4444"
    : isValid
      ? "#22c55e"
      : focused
        ? accent
        : "rgba(0,0,0,0.10)";
  const shadowColor = errorMsg
    ? "rgba(239,68,68,0.12)"
    : isValid
      ? "rgba(34,197,94,0.12)"
      : `${accent}18`;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}
    >
      {/* Label */}
      <label
        style={{
          fontSize: "clamp(11px,0.62vw,13px)",
          fontWeight: 800,
          color: errorMsg
            ? "#ef4444"
            : isValid
              ? "#16a34a"
              : focused
                ? accent
                : C.textSub,
          letterSpacing: "1px",
          display: "flex",
          alignItems: "center",
          gap: 4,
          transition: "color 0.2s",
        }}
      >
        <span style={{ fontSize: 11 }}>{icon}</span>
        {label}
        {/* required star */}
        <span
          style={{
            color: isValid ? "#22c55e" : errorMsg ? "#ef4444" : "#f59e0b",
            fontSize: 13,
            lineHeight: 1,
          }}
        >
          *
        </span>
        {/* tick / cross */}
        {touched && !isEmpty && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            style={{ marginLeft: "auto", fontSize: 11 }}
          >
            {isValid ? "✅" : "❌"}
          </motion.span>
        )}
      </label>

      {/* Input */}
      <motion.div
        animate={focused ? { scale: 1.01 } : { scale: 1 }}
        transition={{ duration: 0.15 }}
        style={{ position: "relative" }}
      >
        <input
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          inputMode={NUMERIC_FIELDS.has(fieldKey) ? "numeric" : "text"}
          style={{
            width: "100%",
            background: focused ? accBg : "rgba(255,255,255,0.75)",
            border: `1.5px solid ${borderColor}`,
            borderRadius: 8,
            padding: "8px 12px",
            color: C.text,
            fontSize: 13,
            fontWeight: 600,
            outline: "none",
            boxSizing: "border-box",
            transition: "all 0.2s",
            boxShadow: focused
              ? `0 0 0 3px ${shadowColor}`
              : "0 1px 3px rgba(0,0,0,0.06)",
            cursor: "text",
          }}
        />
        <AnimatePresence>
          {focused && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              style={{
                position: "absolute",
                bottom: 0,
                left: "10%",
                right: "10%",
                height: 2,
                background: `linear-gradient(90deg,transparent,${borderColor},transparent)`,
                borderRadius: "0 0 8px 8px",
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Error */}
      <AnimatePresence>
        {errorMsg && (
          <motion.span
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#ef4444",
              letterSpacing: "0.4px",
              paddingLeft: 2,
            }}
          >
            ⚠ {errorMsg}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Row ───────────────────────────────────────────────────────────────────── */
const Row = ({ children, cols }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(${cols || children.length},1fr)`,
      gap: 12,
    }}
  >
    {children}
  </div>
);

/* ── Radio ─────────────────────────────────────────────────────────────────── */
const Radio = ({ value, label, selected, color, bg, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.04, y: -1 }}
    whileTap={{ scale: 0.97 }}
    onClick={() => onClick(value)}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 7,
      padding: "7px 14px",
      borderRadius: 8,
      cursor: "pointer",
      background: selected ? bg : "rgba(0,0,0,0.03)",
      border: `1.5px solid ${selected ? color : "rgba(0,0,0,0.10)"}`,
      transition: "all 0.2s",
      boxShadow: selected ? `0 3px 10px ${color}25` : "none",
    }}
  >
    <div
      style={{
        width: 15,
        height: 15,
        borderRadius: "50%",
        border: `2px solid ${selected ? color : "#ccc"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: color,
              boxShadow: `0 0 4px ${color}`,
            }}
          />
        )}
      </AnimatePresence>
    </div>
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: selected ? color : C.textSub,
        transition: "color 0.2s",
      }}
    >
      {label}
    </span>
  </motion.div>
);

/* ── StatusBadge ───────────────────────────────────────────────────────────── */
const StatusBadge = ({ status, message }) => {
  const config = {
    loading: { bg: "#dbeafe", color: "#1d4ed8", icon: "⏳", text: "Saving..." },
    success: {
      bg: "#d1fae5",
      color: "#065f46",
      icon: "✅",
      text: message || "Saved!",
    },
    error: {
      bg: "#fee2e2",
      color: "#991b1b",
      icon: "❌",
      text: message || "Save failed",
    },
  }[status];
  if (!config) return null;
  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: 8,
        background: config.bg,
        color: config.color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      <span>{config.icon}</span>
      {config.text}
    </motion.div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════════════════════ */
export default function FirmConfiguration() {
  const [dealer, setDealer] = useState("gst");
  const [apiStatus, setApiStatus] = useState(null);
  const [apiMessage, setApiMessage] = useState("");
  const [fetchLoading, setFetchLoading] = useState(true);
  const [originalFirmName, setOriginalFirmName] = useState("");

  const [form, setForm] = useState({
    firmName: "",
    firmCode: "",
    address1: "",
    address2: "",
    pincode: "",
    city: "",
    gstin: "",
    financialYear: "",
    financialYearCode: "",
    firmPhone: "",
    ownerName: "",
    mobile1: "",
    mobile2: "",
    email: "",
    branchName: "",
    clientId: "",
  });

  /* ── GET ── */
  useEffect(() => {
    const fetchFirmData = async () => {
      setFetchLoading(true);
      try {
        const res = await fetch(
          `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=Firm_Configure`,
          { method: "GET", headers: API_HEADERS },
        );
        const data = await res.json();
        const d = Array.isArray(data) ? data[0] : data;
        if (d) {
          const dealerType = d.COMPOSITIONDLR
            ? "composition"
            : d.REGULAR
              ? "regular"
              : "gst";
          setDealer(dealerType);
          setOriginalFirmName(d.FIRMNAME || "");
          setForm({
            firmName: d.FIRMNAME || "",
            firmCode: d.CODE || "",
            address1: d.ADD1 || "",
            address2: d.ADD2 || "",
            pincode: d.EMAIL1 || "",
            city: d.CITY || "",
            gstin: d.TINNO || "",
            financialYear: d.FYEAR || "",
            financialYearCode: d.FCODE || "",
            firmPhone: d.FMOBILE || "",
            ownerName: d.OWNERNAME || "",
            mobile1: d.MOBILE || "",
            mobile2: d.MOBILE1 || "",
            email: d.EMAIL || "",
            branchName: d.BRANCHNAME || "",
            clientId: d.CLIENTID || "",
          });
        }
      } catch (err) {
        console.error("Failed to load firm data:", err);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchFirmData();
  }, []);

  /* set() — unchanged; Field.handleChange does the filtering */
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  /* ── SAVE ── */
  const handleSave = async () => {
    try {
      setApiStatus("loading");
      await axios.post(
        `${CREATE_jwel}/api/Master/CommonUpdateWithWhere`,
        {
          tableName: "Firm_Configure",
          updateString: buildUpdateString(form, dealer),
          whereString: `FIRMNAME='${originalFirmName}'`,
        },
        { headers: { tenantName: TENANT_NAME } },
      );
      setApiStatus("success");
      setApiMessage("Configuration saved!");
      setTimeout(() => setApiStatus(null), 3000);
    } catch {
      setApiStatus("error");
      setApiMessage("Update failed");
      setTimeout(() => setApiStatus(null), 4000);
    }
  };

  /* SVG patterns */
  const checkPatternSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60'><text x='10' y='42' font-size='32' fill='rgba(99,102,241,0.07)' font-family='Arial'>✓</text></svg>`;
  const checkPatternUrl = `url("data:image/svg+xml,${encodeURIComponent(checkPatternSvg)}")`;
  const stripesSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'>${[0, 16, 32, 48, 64, 80, 96, 112].map((i) => `<line x1='${i}' y1='0' x2='${i + 20}' y2='120' stroke='rgba(99,102,241,0.06)' stroke-width='8'/>`).join("")}</svg>`;
  const stripesUrl = `url("data:image/svg+xml,${encodeURIComponent(stripesSvg)}")`;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "28px 16px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        input::placeholder { color:#b0afc8 !important; }
        * { box-sizing:border-box; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:rgba(99,102,241,0.35); border-radius:3px; }
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(20px,-18px) scale(1.08);} }
        .lgt-orb { position:fixed; border-radius:50%; pointer-events:none; filter:blur(55px); }
      `}</style>

      {/* Orbs */}
      {[
        {
          w: 320,
          h: 320,
          bg: "rgba(167,139,250,0.22)",
          t: "-60px",
          l: "-60px",
          dur: "14s",
        },
        {
          w: 260,
          h: 260,
          bg: "rgba(13,148,136,0.15)",
          b: "-40px",
          r: "-40px",
          dur: "18s",
          rev: true,
        },
        {
          w: 200,
          h: 200,
          bg: "rgba(249,115,22,0.12)",
          t: "40%",
          l: "5%",
          dur: "12s",
          delay: "2s",
        },
        {
          w: 180,
          h: 180,
          bg: "rgba(236,72,153,0.12)",
          t: "20%",
          r: "8%",
          dur: "16s",
          delay: "1s",
        },
      ].map((o, i) => (
        <div
          key={i}
          className="lgt-orb"
          style={{
            width: o.w,
            height: o.h,
            background: o.bg,
            top: o.t,
            bottom: o.b,
            left: o.l,
            right: o.r,
            animation: `orbFloat ${o.dur} ease-in-out infinite${o.rev ? " reverse" : ""}${o.delay ? ` ${o.delay}` : ""}`,
          }}
        />
      ))}

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 36, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        style={{
          width: "100%",
          maxWidth: 820,
          borderRadius: 22,
          background:
            "linear-gradient(160deg,rgba(255,255,255,0.95) 0%,rgba(243,240,255,0.97) 40%,rgba(236,253,245,0.96) 75%,rgba(255,251,235,0.97) 100%)",
          border: "1px solid rgba(99,102,241,0.18)",
          boxShadow:
            "0 20px 60px rgba(99,102,241,0.14),0 4px 16px rgba(0,0,0,0.07)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Patterns */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: checkPatternUrl,
            backgroundRepeat: "repeat",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 140,
            height: 140,
            backgroundImage: stripesUrl,
            backgroundRepeat: "repeat",
            opacity: 0.6,
            pointerEvents: "none",
            zIndex: 0,
            borderRadius: "0 22px 0 0",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "radial-gradient(rgba(167,139,250,0.18),transparent 70%)",
            top: -50,
            right: 80,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 160,
            height: 160,
            borderRadius: "50%",
            background:
              "radial-gradient(rgba(13,148,136,0.13),transparent 70%)",
            bottom: -40,
            left: 60,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Header */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            padding: "18px 24px 16px",
            background:
              "linear-gradient(90deg,rgba(99,102,241,0.10) 0%,rgba(13,148,136,0.07) 50%,rgba(249,115,22,0.06) 100%)",
            borderBottom: "1px solid rgba(99,102,241,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                flexShrink: 0,
                background: `linear-gradient(135deg,${C.purple},${C.pink},${C.blue})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 21,
                boxShadow: `0 6px 20px ${C.purple}44`,
              }}
            >
              ⚙️
            </motion.div>
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  letterSpacing: "-0.4px",
                  background: `linear-gradient(90deg,${C.purple} 0%,${C.indigo} 50%,${C.pink} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Firm Configuration
              </div>
              <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                Manage your firm's profile & business settings
              </div>
            </div>
          </div>
          <AnimatePresence>
            {apiStatus && (
              <StatusBadge status={apiStatus} message={apiMessage} />
            )}
          </AnimatePresence>
        </div>

        <ShimmerLine
          colors={[C.purple, C.blue, C.teal, C.amber, C.pink, C.purple]}
        />

        {/* Form Body */}
        <div
          style={{
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Loading overlay */}
          <AnimatePresence>
            {fetchLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  background: "rgba(255,255,255,0.75)",
                  backdropFilter: "blur(3px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      border: `3px solid ${C.purple3}`,
                      borderTop: `3px solid ${C.purple}`,
                    }}
                  />
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: C.purple }}
                  >
                    Loading firm data...
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 1 — FIRM IDENTITY */}
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.08, duration: 0.4 }}
          >
            <SectionDivider icon="🏢" title="Firm Identity" accent={C.purple} />
            <Row cols={2}>
              <Field
                fieldKey="firmName"
                label="Firm Name"
                icon="🏷️"
                placeholder="Firm Name"
                value={form.firmName}
                onChange={set("firmName")}
                accent={C.purple}
                accBg={C.purple3}
              />
              <Field
                fieldKey="firmCode"
                label="Firm Code"
                icon="🔑"
                placeholder="Firm Code"
                value={form.firmCode}
                onChange={set("firmCode")}
                accent={C.purple}
                accBg={C.purple3}
              />
            </Row>
          </motion.div>

          <div style={{ margin: "10px 0" }} />

          {/* 2 — ADDRESS */}
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.14, duration: 0.4 }}
          >
            <SectionDivider icon="📍" title="Address" accent={C.teal} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 2,
              }}
            >
              <Row cols={2}>
                <Field
                  fieldKey="address1"
                  label="Address Line 1"
                  icon="🏠"
                  placeholder="Address Line 1"
                  value={form.address1}
                  onChange={set("address1")}
                  accent={C.teal}
                  accBg={C.teal2}
                />
                <Field
                  fieldKey="address2"
                  label="Address Line 2"
                  icon="🏘️"
                  placeholder="Address Line 2"
                  value={form.address2}
                  onChange={set("address2")}
                  accent={C.teal}
                  accBg={C.teal2}
                />
              </Row>
              <Row cols={2}>
                <Field
                  fieldKey="pincode"
                  label="Pincode"
                  icon="📮"
                  placeholder="6-digit pincode"
                  value={form.pincode}
                  onChange={set("pincode")}
                  accent={C.teal}
                  accBg={C.teal2}
                />
                <Field
                  fieldKey="city"
                  label="City"
                  icon="🌆"
                  placeholder="City"
                  value={form.city}
                  onChange={set("city")}
                  accent={C.teal}
                  accBg={C.teal2}
                />
              </Row>
            </div>
          </motion.div>

          <div style={{ margin: "10px 0" }} />

          {/* 3 — TAX & FINANCIAL */}
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <SectionDivider
              icon="📊"
              title="Tax & Financial"
              accent={C.amber}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 2,
              }}
            >
              <Field
                fieldKey="gstin"
                label="GSTIN"
                icon="🧾"
                placeholder="e.g. 22AAAAA0000A1Z5"
                value={form.gstin}
                onChange={set("gstin")}
                accent={C.amber}
                accBg={C.amber2}
              />
              <Row cols={2}>
                <Field
                  fieldKey="financialYear"
                  label="Financial Year"
                  icon="📅"
                  placeholder="e.g. 2026"
                  value={form.financialYear}
                  onChange={set("financialYear")}
                  accent={C.amber}
                  accBg={C.amber2}
                />
                <Field
                  fieldKey="financialYearCode"
                  label="Year Code"
                  icon="🔢"
                  placeholder="e.g. 2627"
                  value={form.financialYearCode}
                  onChange={set("financialYearCode")}
                  accent={C.amber}
                  accBg={C.amber2}
                />
              </Row>
            </div>
          </motion.div>

          <div style={{ margin: "10px 0" }} />

          {/* 4 — CONTACT */}
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.26, duration: 0.4 }}
          >
            <SectionDivider icon="📞" title="Contact Details" accent={C.blue} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 2,
              }}
            >
              <Row cols={2}>
                <Field
                  fieldKey="firmPhone"
                  label="Firm Phone"
                  icon="☎️"
                  placeholder="10-digit number"
                  value={form.firmPhone}
                  onChange={set("firmPhone")}
                  accent={C.blue}
                  accBg={C.blue2}
                />
                <Field
                  fieldKey="ownerName"
                  label="Owner Name"
                  icon="👤"
                  placeholder="Owner Name"
                  value={form.ownerName}
                  onChange={set("ownerName")}
                  accent={C.blue}
                  accBg={C.blue2}
                />
              </Row>
              <Row cols={3}>
                <Field
                  fieldKey="mobile1"
                  label="Mobile No. 1"
                  icon="📱"
                  placeholder="10-digit number"
                  value={form.mobile1}
                  onChange={set("mobile1")}
                  accent={C.blue}
                  accBg={C.blue2}
                />
                <Field
                  fieldKey="mobile2"
                  label="Mobile No. 2"
                  icon="📱"
                  placeholder="10-digit number"
                  value={form.mobile2}
                  onChange={set("mobile2")}
                  accent={C.blue}
                  accBg={C.blue2}
                />
                <Field
                  fieldKey="email"
                  label="Email ID"
                  icon="✉️"
                  placeholder="firm@example.com"
                  value={form.email}
                  onChange={set("email")}
                  accent={C.blue}
                  accBg={C.blue2}
                />
              </Row>
            </div>
          </motion.div>

          <div style={{ margin: "10px 0" }} />

          {/* 5 — BRANCH & BILLING */}
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.32, duration: 0.4 }}
          >
            <SectionDivider
              icon="🏪"
              title="Branch & Billing"
              accent={C.indigo}
            />
            <Row cols={2}>
              <Field
                fieldKey="branchName"
                label="Branch Name"
                icon="🌿"
                placeholder="Branch Name"
                value={form.branchName}
                onChange={set("branchName")}
                accent={C.indigo}
                accBg={C.indigo2}
              />
              <Field
                fieldKey="clientId"
                label="Client ID"
                icon="🆔"
                placeholder="Client ID"
                value={form.clientId}
                onChange={set("clientId")}
                accent={C.indigo}
                accBg={C.indigo2}
              />
            </Row>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          style={{
            position: "relative",
            zIndex: 1,
            padding: "14px 24px",
            borderTop: "1px solid rgba(99,102,241,0.12)",
            background:
              "linear-gradient(90deg,rgba(99,102,241,0.07) 0%,rgba(13,148,136,0.06) 50%,rgba(245,158,11,0.05) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: C.textSub,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Dealer Type
            </span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Radio
                value="composition"
                label="Composition Dealer"
                color={C.amber}
                bg={C.amber2}
                selected={dealer === "composition"}
                onClick={setDealer}
              />
              <Radio
                value="regular"
                label="Regular"
                color={C.blue}
                bg={C.blue2}
                selected={dealer === "regular"}
                onClick={setDealer}
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <motion.button
              onClick={handleSave}
              disabled={apiStatus === "loading"}
              whileHover={
                apiStatus !== "loading"
                  ? { y: -3, boxShadow: `0 10px 26px ${C.teal}44` }
                  : {}
              }
              whileTap={apiStatus !== "loading" ? { scale: 0.96 } : {}}
              style={{
                padding: "9px 26px",
                borderRadius: 9,
                border: "none",
                background:
                  apiStatus === "loading"
                    ? "#94a3b8"
                    : `linear-gradient(135deg,#0a7a74,${C.teal})`,
                color: "#fff",
                fontWeight: 800,
                fontSize: 13,
                cursor: apiStatus === "loading" ? "not-allowed" : "pointer",
                boxShadow: `0 4px 14px ${C.teal}33`,
                letterSpacing: "0.5px",
                position: "relative",
                overflow: "hidden",
                transition: "background 0.2s",
              }}
            >
              <AnimatePresence mode="wait">
                {apiStatus === "loading" ? (
                  <motion.span
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    ⏳ Saving...
                  </motion.span>
                ) : (
                  <motion.span
                    key="save"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    💾 Save
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </motion.div>

        <ShimmerLine
          colors={[C.teal, C.blue, C.indigo, C.purple, C.pink, C.teal]}
        />
      </motion.div>

      {/* Toast */}
      <AnimatePresence>
        {(apiStatus === "success" || apiStatus === "error") && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30 }}
            style={{
              position: "fixed",
              bottom: 28,
              right: 28,
              zIndex: 9999,
              background:
                apiStatus === "success"
                  ? `linear-gradient(135deg,#0a7a74,${C.teal})`
                  : "linear-gradient(135deg,#b91c1c,#ef4444)",
              borderRadius: 12,
              padding: "12px 20px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              boxShadow:
                apiStatus === "success"
                  ? `0 12px 36px ${C.teal}44`
                  : "0 12px 36px rgba(239,68,68,0.4)",
              border: `1px solid ${apiStatus === "success" ? C.teal : "#f87171"}77`,
            }}
          >
            <motion.span
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 0.5 }}
              style={{ fontSize: 18 }}
            >
              {apiStatus === "success" ? "✅" : "❌"}
            </motion.span>
            <div>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>
                {apiStatus === "success"
                  ? "Configuration Saved!"
                  : "Save Failed"}
              </div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>
                {apiMessage ||
                  (apiStatus === "success"
                    ? "All changes have been applied."
                    : "Please check your connection.")}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}